import {compareAsc, parseISO} from 'date-fns';
import {isFeatureEnabled} from 'feature-flags';

import {isEmpty, isNil} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {useGetBusinessCaseQuery} from '@dms/api/sales';
import {EntityResourceIds} from '@dms/api/shared';
import {featureFlags} from '@dms/feature-flags';
import {
  CommentsTab,
  ActivitySidebarAction,
  ActivitySidebarV2,
  CommunicationThreadTab,
  CustomerChat,
  CustomerChatIndicator,
  useChatSubscribe,
  useGetCurrentChatUser,
} from '@dms/features/activity-panel';
import {buildCustomNoticesSidebarAction} from '@dms/features/custom-notices';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {useBcToBuyingOnlineOffer, usePermissions} from '@dms/shared';

import {buildArray} from 'shared/web';

import {ActivityTab} from './components/ActivityTab';

interface BusinessCaseActivityPanelProps {
  businessCaseId: string;
}

// eslint-disable-next-line complexity -- Pre-existing violation
export function BusinessCaseActivityPanel(props: BusinessCaseActivityPanelProps) {
  const {data: businessCaseData} = useGetBusinessCaseQuery({businessCaseId: props.businessCaseId});

  const {data: businessCaseParticipation} = useGetParticipationQuery({
    recordId: props.businessCaseId,
    resourceId: EntityResourceIds.businessCase,
  });

  const [canReadOnlineChat] = usePermissions({
    permissionKeys: ['readOnlineChat'],
    scopes: {
      readOnlineChat: {
        participation: businessCaseParticipation,
        branchId: businessCaseData?.branchId,
      },
    },
  });

  const [canReadSalesActivities] = usePermissions({
    permissionKeys: ['readSalesActivities'],
  });

  const chatConversations = businessCaseData?.chatConversations || [];
  const isBuyingBusinessCase = businessCaseData?.businessCaseType === 'BUYING';
  const canOpenBuyingOfferDialog =
    isNotNilOrEmpty(businessCaseData?.offers[0]?.purchaseVehicles?.[0]?.vehicleId) &&
    isNotNilOrEmpty(businessCaseParticipation?.owner?.userId);
  const {
    canSendOffer: canSendBuyingOffer,
    canResendOffer: canResendBuyingOffer,
    isOnlineOfferCreated: isBuyingOnlineOfferCreated,
    isBuyingOnlineOfferEnabled,
  } = useBcToBuyingOnlineOffer(props.businessCaseId);
  const canStartBuyingChat =
    canOpenBuyingOfferDialog &&
    isBuyingOnlineOfferEnabled &&
    (canSendBuyingOffer || (isBuyingOnlineOfferCreated && canResendBuyingOffer));

  const sortedChatConversations = chatConversations.toSorted((a, b) =>
    compareAsc(parseISO(a.createdAt), parseISO(b.createdAt))
  );
  const sortedChatConversationsIds = sortedChatConversations.map(
    ({conversationId}) => conversationId
  );
  const activeConversation = chatConversations.find((item) => item.isActive);

  const currentUserInConversation = useGetCurrentChatUser({
    conversationId: activeConversation?.conversationId,
  });

  const {isTyping} = useChatSubscribe({
    conversationId: activeConversation?.conversationId,
    currentUserId: currentUserInConversation?.id,
    businessCaseId: props.businessCaseId,
  });

  const actions = buildArray<ActivitySidebarAction>()
    .when(canReadSalesActivities, {
      id: 'activity',
      label: i18n.t('entity.activityPanel.activity.title'),
      icon: 'sidebar/activity',
      component: <ActivityTab businessCaseId={props.businessCaseId} />,
      'data-testid': testIds.businessCase.selling('activity-menu-button'),
    })
    .when(
      canReadSalesActivities &&
        canReadOnlineChat &&
        (!isBuyingBusinessCase || !isEmpty(chatConversations) || canStartBuyingChat),
      {
        id: 'chat',
        label: i18n.t('entity.activityPanel.chat.title'),
        icon: 'communication/customer_chat',
        indicator: (
          <CustomerChatIndicator
            conversationId={activeConversation?.conversationId || ''}
            isTyping={isTyping}
          />
        ),
        'data-testid': testIds.businessCase.selling('chat-menu-button'),
        component: (
          // TODO: rethink if activeConversationId shouldn't be nullable, types would be better
          <CustomerChat
            activeConversationId={activeConversation?.conversationId ?? ''}
            conversationIds={sortedChatConversationsIds}
            businessCaseId={props.businessCaseId}
            customerId={businessCaseData?.customerId}
            isBuyingBusinessCase={isBuyingBusinessCase}
            canOpenBuyingOfferDialog={canOpenBuyingOfferDialog}
            isTyping={isTyping}
            isChattingDisabled={
              isNil(businessCaseData?.offerId) ||
              isEmpty(chatConversations) ||
              isNil(businessCaseData?.onlineOfferFirstSentAt)
            }
          />
        ),
        disablePadding: true,
      }
    )
    .when(
      canReadSalesActivities &&
        isFeatureEnabled(featureFlags.MESSAGE_SYNCHRONIZATION) &&
        isNotNilOrEmpty(businessCaseData?.customerId),
      {
        id: 'communication',
        label: i18n.t('entity.activityPanel.communicationThread.title'),
        icon: 'communication/thread',
        component: (
          <CommunicationThreadTab
            customerId={businessCaseData?.customerId ?? ''}
            recordId={props.businessCaseId}
            resourceId={EntityResourceIds.businessCase}
          />
        ),
        disablePadding: true,
      }
    )
    .when(canReadSalesActivities, {
      id: 'comments',
      label: i18n.t('entity.activityPanel.comments.title'),
      icon: 'communication/comment',
      component: <CommentsTab businessCaseId={props.businessCaseId} />,
    })
    .whenFeatureEnabled(
      featureFlags.CUSTOM_NOTICES,
      buildCustomNoticesSidebarAction({
        resourceId: EntityResourceIds.businessCase,
        recordId: props.businessCaseId,
        'data-testid': testIds.businessCase.selling('customNotices'),
      })
    );

  return <ActivitySidebarV2 actions={actions} />;
}
